import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { InformationCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { HAND_COMBO_METADATA } from '@solante/shared';
import { useTutorialMatchStore } from '../../state/tutorialMatchStore';
import { soundManager } from '../../utils/sounds';

// ── What You'll Learn cards ───────────────────────────────────────────────────

interface LearnCard {
  title: string;
  borderColor: string;
  detail: React.ReactNode;
}

const learnCards: LearnCard[] = [
  {
    title: 'Matchmaking & Escrow',
    borderColor: 'border-l-cyan-400',
    detail: (
      <>
        Both players wager the same amount of SOL. Funds are locked in an on-chain escrow smart
        contract before the match begins. The winner receives 97% of the total pot; 3% goes to the
        treasury. Your wallet signs a single transaction to create or join a match.
      </>
    ),
  },
  {
    title: 'The Deck & Suits',
    borderColor: 'border-l-yellow-400',
    detail: (
      <>
        52 cards across 4 crypto-themed suits: Sol (gold), Mint (cyan), Burn (pink), and Stake
        (purple). 13 ranks per suit (2 through Ace). The deck is shuffled using a deterministic PRNG
        seeded by both the server and client seeds for provable fairness.
      </>
    ),
  },
  {
    title: 'Card Combos',
    borderColor: 'border-l-pink-400',
    detail: (
      <div className="space-y-1.5">
        <p>
          Poker-style combos determine your base score and multiplier. Stronger combos score
          exponentially more:
        </p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-white/60">
          {[...HAND_COMBO_METADATA].reverse().map((c) => (
            <div key={c.comboId} className="flex justify-between">
              <span>{c.name}</span>
              <span className="text-white/40">
                {c.score} &times; {c.multiplier}x
              </span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    title: 'Turn-by-Turn Play',
    borderColor: 'border-l-cyan-400',
    detail: (
      <>
        Each turn you receive 6 cards. Select 1 to 5 cards to form your best combo and play them.
        The game auto-detects the best poker hand from your selection. In real matches, you have 45
        seconds per turn. Players alternate turns until the match ends.
      </>
    ),
  },
  {
    title: 'Scoring & Multipliers',
    borderColor: 'border-l-pink-400',
    detail: (
      <>
        Your turn score = base combo score &times; combo multiplier + joker bonuses. Higher combos
        earn both more base points and a larger multiplier. Scores accumulate across turns toward
        the target of 500 points.
      </>
    ),
  },
  {
    title: 'Hearts & Chips',
    borderColor: 'border-l-red-400',
    detail: (
      <>
        You start with 3 hearts and 320 chips. Playing a hand that scores 0 (no valid combo) costs 1
        heart. Losing all 3 hearts means defeat regardless of score. Chips are earned through play
        and spent in the shop on jokers and consumables.
      </>
    ),
  },
  {
    title: 'Jokers & Loadouts',
    borderColor: 'border-l-purple-400',
    detail: (
      <>
        Jokers are permanent power-ups purchased from the shop. They activate every hand and effects
        stack. Types include flat bonuses (+points), percentage bonuses (+% to score),
        combo-specific bonuses, and chip generators. You can hold up to 5 joker slots.
      </>
    ),
  },
  {
    title: 'The Shop',
    borderColor: 'border-l-emerald-400',
    detail: (
      <>
        The shop opens every 2 turns. Spend chips to buy jokers (permanent) or consumables
        (one-time). You can reroll the shop offers for increasing chip costs (starting at 50). Click
        &ldquo;Ready&rdquo; when done shopping to continue the match.
      </>
    ),
  },
  {
    title: 'Sabotage Cards',
    borderColor: 'border-l-amber-400',
    detail: (
      <>
        10 PvP sabotage cards add a competitive twist: Priority Fee Spike (-40 points), Suit Lock
        (locks a random suit), MEV Sandwich (steals chips), Wallet Drain (-1 heart), and more.
        Shields can block incoming sabotage.
      </>
    ),
  },
  {
    title: 'Anomalies',
    borderColor: 'border-l-orange-400',
    detail: (
      <>
        Random environmental events that affect both players. Examples include double-scoring for a
        specific suit, inverted score modifiers, or a sudden boss challenge. Anomalies appear
        dynamically and last a few turns.
      </>
    ),
  },
  {
    title: 'Tempo & Speed',
    borderColor: 'border-l-yellow-400',
    detail: (
      <>
        Playing quickly (under 20 seconds) builds a tempo streak. Reaching a 3-turn streak grants
        bonus points (up to +25). Fast, decisive play is rewarded while still leaving plenty of time
        to think.
      </>
    ),
  },
  {
    title: 'Victory Conditions',
    borderColor: 'border-l-yellow-400',
    detail: (
      <>
        The match ends when a player reaches the target score (500 points in tutorial, 1,000 in real
        matches), an opponent loses all 3 hearts, or turn 25 is reached (highest score wins).
        Forfeiting concedes the match immediately.
      </>
    ),
  },
];

// ── Tooltip ───────────────────────────────────────────────────────────────────

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [open, setOpen] = useState(false);

  return (
    <span className="relative inline-block ml-1 align-middle">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="text-white/30 hover:text-arcade-cyan transition-colors"
      >
        <InformationCircleIcon className="h-4 w-4" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-60 rounded-lg border border-white/10 bg-black/95 p-2.5 text-[11px] text-white/70 z-50 pointer-events-none"
          >
            {text}
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
};

// ── Expandable learn card ─────────────────────────────────────────────────────

const LearnCardItem: React.FC<{ card: LearnCard; index: number }> = ({ card, index }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <button
      type="button"
      onClick={() => {
        setExpanded((v) => !v);
        soundManager.play('click');
      }}
      className={`w-full text-left rounded-xl border-l-4 ${card.borderColor} border border-white/10 bg-black/40 px-4 py-3 transition-colors hover:bg-white/5`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-white/30">{index + 1}.</span>
          <span className="text-sm font-semibold text-white/90">{card.title}</span>
        </div>
        <ChevronDownIcon
          className={`h-4 w-4 text-white/30 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-2 text-xs text-white/60 leading-relaxed">{card.detail}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

export const TutorialPage: React.FC = () => {
  const navigate = useNavigate();
  const { fakeWagerAmount, setFakeWagerAmount } = useTutorialMatchStore();
  const [starting, setStarting] = useState(false);

  const handleStart = () => {
    if (starting) return;
    setStarting(true);
    soundManager.play('select');
    navigate('/tutorial/match');
  };

  return (
    <div className="min-h-screen p-4 pt-20 pb-24">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <h1 className="neon-text font-display text-3xl font-bold">TUTORIAL</h1>
          <p className="mt-2 text-white/50">Learn every aspect of Solante — no real SOL required</p>
        </motion.div>

        {/* Main grid */}
        <motion.section
          className="arcade-panel pixel-corners grid gap-6 rounded-xl p-4 sm:p-8 lg:grid-cols-[2fr,3fr]"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          {/* Left column — Tutorial Wager Setup */}
          <div>
            <h2 className="neon-text font-display text-2xl font-semibold">Tutorial Wager Setup</h2>
            <p className="mt-2 text-sm text-white/70">
              This simulates the real matchmaking flow. No real SOL is used.
            </p>

            <div className="mt-6 space-y-4">
              {/* Currency */}
              <label className="block text-sm">
                <span className="text-white/60">
                  Currency
                  <InfoTooltip text="In a real match, your SOL is locked in an on-chain escrow smart contract until the match ends." />
                </span>
                <select
                  disabled
                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-arcade-cyan/50 focus:outline-none focus:ring-1 focus:ring-arcade-cyan/30"
                >
                  <option value="SOL">SOL</option>
                  <option value="USDC" disabled>
                    USDC (soon)
                  </option>
                </select>
              </label>

              {/* Wager amount */}
              <label className="block text-sm">
                <span className="text-white/60">
                  Wager Amount (SOL)
                  <InfoTooltip text="Both players put up the same amount. This is just for practice — no real SOL is used." />
                </span>
                <select
                  value={fakeWagerAmount}
                  onChange={(e) => {
                    setFakeWagerAmount(Number(e.target.value));
                    soundManager.play('click');
                  }}
                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-arcade-cyan/50 focus:outline-none focus:ring-1 focus:ring-arcade-cyan/30"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <option key={n} value={n}>
                      {n} SOL
                    </option>
                  ))}
                </select>
              </label>

              {/* Info box */}
              <div className="rounded-2xl border border-arcade-cyan/30 bg-arcade-cyan/10 p-4 text-sm">
                <p className="font-semibold text-arcade-cyan">Tutorial Mode</p>
                <ul className="mt-2 space-y-1 text-white/80">
                  <li>Target: 500 points to win</li>
                  <li>Guided coaching at every step</li>
                  <li>No server needed — runs instantly</li>
                  <li>Different hands every time</li>
                </ul>
              </div>

              {/* Start button */}
              <button
                type="button"
                onClick={handleStart}
                disabled={starting}
                className="arcade-btn pixel-corners flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3 font-display text-xs font-semibold uppercase tracking-wide text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {starting ? 'Starting...' : 'Start Tutorial Match'}
              </button>
            </div>
          </div>

          {/* Right column — What You'll Learn */}
          <div className="rounded-3xl border border-white/10 bg-black/40 p-4 sm:p-6">
            <h3 className="mb-4 text-sm font-semibold text-white/80">What You&apos;ll Learn</h3>
            <div className="h-[340px] space-y-2 overflow-y-auto pr-2 sm:h-[480px]">
              {learnCards.map((card, i) => (
                <LearnCardItem key={card.title} card={card} index={i} />
              ))}
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};
